/**
 * CampaignTimeline - Visual timeline of campaign steps
 */
import { Mail, MessageSquare, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { CampaignStep, CampaignStepStats } from '@respectly/shared-types';

// ============================================
// Config
// ============================================

const stepIcons = {
  email: Mail,
  sms: MessageSquare,
  wait: Clock,
};

const stepStyles = {
  email: {
    bg: 'bg-indigo-500',
    ring: 'ring-indigo-200 dark:ring-indigo-800',
    lightBg: 'bg-indigo-50 dark:bg-indigo-900/20',
    text: 'text-indigo-600 dark:text-indigo-400',
  },
  sms: {
    bg: 'bg-purple-500',
    ring: 'ring-purple-200 dark:ring-purple-800',
    lightBg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-600 dark:text-purple-400',
  },
  wait: {
    bg: 'bg-amber-500',
    ring: 'ring-amber-200 dark:ring-amber-800',
    lightBg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-600 dark:text-amber-400',
  },
};

// ============================================
// Types
// ============================================

interface CampaignTimelineProps {
  steps: CampaignStep[];
  stepStats?: CampaignStepStats[];
  currentStepIndex?: number;
  className?: string;
}

// ============================================
// Component
// ============================================

export function CampaignTimeline({
  steps,
  stepStats,
  currentStepIndex,
  className,
}: CampaignTimelineProps) {
  if (steps.length === 0) {
    return (
      <div className={cn('text-center py-8 text-slate-500 dark:text-slate-400 text-sm', className)}>
        No steps defined for this campaign.
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <div className="flex items-start gap-3 min-w-fit p-4">
        {steps.map((step, index) => {
          const Icon = stepIcons[step.type];
          const styles = stepStyles[step.type];
          const stats = stepStats?.[index];
          const isCompleted = currentStepIndex !== undefined && index < currentStepIndex;
          const isCurrent = currentStepIndex === index;

          return (
            <div key={step.id || index} className="flex items-start">
              {/* Step node */}
              <div className="flex flex-col items-center min-w-[100px]">
                <div
                  className={cn(
                    'relative h-11 w-11 rounded-full flex items-center justify-center transition-all',
                    isCompleted ? 'bg-emerald-500' : styles.bg,
                    isCurrent && `ring-4 ${styles.ring}`,
                    !isCompleted && !isCurrent && 'opacity-90'
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5 text-white" />
                  ) : (
                    <Icon className="h-5 w-5 text-white" />
                  )}
                </div>

                <div className="mt-2.5 text-center w-full px-1">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    {step.type === 'email' ? 'Email' : step.type === 'sms' ? 'SMS' : `Wait ${step.delay ?? 0}d`}
                  </div>

                  {/* Subject preview for email */}
                  {step.type === 'email' && step.emailSubject && (
                    <div className="text-[10px] text-slate-400 mt-0.5 line-clamp-2 leading-tight">
                      {step.emailSubject}
                    </div>
                  )}

                  {/* SMS content preview */}
                  {step.type === 'sms' && step.smsContent && (
                    <div className="text-[10px] text-slate-400 mt-0.5 line-clamp-2 leading-tight">
                      {step.smsContent.slice(0, 40)}...
                    </div>
                  )}

                  {/* Stats */}
                  {stats && (
                    <div className="mt-1.5 space-y-0.5">
                      <div className="text-[10px] text-slate-400">
                        <span className="font-semibold text-slate-600 dark:text-slate-300">{stats.reached}</span> reached
                      </div>
                      <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                        {stats.completed} done
                      </div>
                      {stats.exitRate > 0 && (
                        <div className="text-[10px] text-amber-500">
                          {(stats.exitRate * 100).toFixed(0)}% exit
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Connector */}
              {index < steps.length - 1 && (
                <div className="flex items-center h-11 px-1 mt-0">
                  <div className={cn(
                    'h-0.5 w-8',
                    isCompleted ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'
                  )} />
                  <ArrowRight className={cn(
                    'h-3.5 w-3.5',
                    isCompleted ? 'text-emerald-400' : 'text-slate-300 dark:text-slate-600'
                  )} />
                  <div className={cn(
                    'h-0.5 w-8',
                    isCompleted ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'
                  )} />
                </div>
              )}
            </div>
          );
        })}

        {/* End indicator */}
        <div className="flex flex-col items-center min-w-[80px]">
          <div className="h-11 w-11 rounded-full bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-300 dark:border-emerald-700 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="mt-2.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            Complete
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Compact Version
// ============================================

export function CampaignTimelineCompact({
  steps,
  className,
}: {
  steps: CampaignStep[];
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-1.5 flex-wrap', className)}>
      {steps.map((step, index) => {
        const Icon = stepIcons[step.type];
        const styles = stepStyles[step.type];

        return (
          <div key={step.id || index} className="flex items-center">
            <div
              title={
                step.type === 'wait'
                  ? `Wait ${step.delay ?? 0} day${(step.delay ?? 0) !== 1 ? 's' : ''}`
                  : step.type === 'email'
                  ? step.emailSubject || 'Email'
                  : 'SMS'
              }
              className={cn(
                'h-6 w-6 rounded-md flex items-center justify-center',
                styles.bg
              )}
            >
              <Icon className="h-3 w-3 text-white" />
            </div>
            {index < steps.length - 1 && (
              <div className="w-3 h-0.5 bg-slate-200 dark:bg-slate-700" />
            )}
          </div>
        );
      })}
      {steps.length === 0 && (
        <span className="text-xs text-slate-400">No steps</span>
      )}
    </div>
  );
}
